import React from 'react';
import { motion } from 'framer-motion';
import { PieChart, Heart, CheckCircle, Mail, FileText, Users, Home, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

const FUND_ALLOCATION = [
  {
    category: 'Community Outreach & Food Pantry',
    percentage: 40,
    color: '#22c55e',
    icon: Heart,
    description: 'Feeding families, community events, and emergency assistance'
  },
  {
    category: 'Ministry Operations & Pastoral Care',
    percentage: 25,
    color: '#3b82f6',
    icon: Users,
    description: 'Staff support, counseling services, and daily operations'
  },
  {
    category: 'Children\'s Ministry Programs',
    percentage: 20,
    color: '#f59e0b',
    icon: BookOpen,
    description: 'Sunday school, youth groups, and educational materials'
  },
  {
    category: 'Facilities & Growth',
    percentage: 15,
    color: '#8b5cf6',
    icon: Home,
    description: 'Building maintenance, utilities, and future expansion'
  }
];

const COMMITMENTS = [
  'We do not pay excessive salaries or administrative bloat',
  'We publish annual giving reports for our community',
  'Every donation is tracked and receipted',
  'We welcome questions — contact us anytime'
];

export default function TransparencyPage() {
  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="transparency-page">
      {/* Banner */}
      <div className="w-full">
        <img src={MINISTRY_BANNER} alt="Living Fruit Ministry" className="w-full h-auto" />
      </div>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-blue-50 to-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
              <PieChart size={32} className="text-blue-600" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4" style={{ color: '#1A1A1A' }}>
              How Your Giving Is Used
            </h1>
            <p className="text-xl" style={{ color: '#1E3A5F' }}>
              We believe in open books and faithful stewardship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-lg leading-relaxed" style={{ color: '#1E3A5F' }}>
              At Living Fruit Ministry, we take financial stewardship seriously. As a 508(c)(1)(A) 
              faith-based nonprofit, we are committed to using every dollar wisely and transparently. 
              Below you will find a general overview of how donations are allocated.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Fund Allocation */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl mb-4" style={{ color: '#1A1A1A' }}>
              Fund Allocation
            </h2>
            <p style={{ color: '#1E3A5F' }}>
              A general overview of how your donations are distributed
            </p>
          </motion.div>

          {/* Visual Bar Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 shadow-xl mb-12"
          >
            <div className="space-y-6">
              {FUND_ALLOCATION.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.category}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${item.color}20` }}
                        >
                          <Icon size={20} style={{ color: item.color }} />
                        </div>
                        <div>
                          <h3 className="font-semibold" style={{ color: '#1A1A1A' }}>
                            {item.category}
                          </h3>
                          <p className="text-sm text-charcoal/60">{item.description}</p>
                        </div>
                      </div>
                      <span className="text-2xl font-bold" style={{ color: item.color }}>
                        {item.percentage}%
                      </span>
                    </div>
                    <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${item.percentage}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Pie Chart Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex justify-center mb-12"
          >
            <div className="relative w-64 h-64">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                {FUND_ALLOCATION.reduce((acc, item, index) => {
                  const startAngle = acc.angle;
                  const sweepAngle = (item.percentage / 100) * 360;
                  const endAngle = startAngle + sweepAngle;
                  
                  const startRad = (startAngle * Math.PI) / 180;
                  const endRad = (endAngle * Math.PI) / 180;
                  
                  const x1 = 50 + 40 * Math.cos(startRad);
                  const y1 = 50 + 40 * Math.sin(startRad);
                  const x2 = 50 + 40 * Math.cos(endRad);
                  const y2 = 50 + 40 * Math.sin(endRad);
                  
                  const largeArc = sweepAngle > 180 ? 1 : 0;
                  
                  acc.paths.push(
                    <path
                      key={item.category}
                      d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                      fill={item.color}
                      className="hover:opacity-80 transition-opacity cursor-pointer"
                    />
                  );
                  acc.angle = endAngle;
                  return acc;
                }, { paths: [], angle: 0 }).paths}
                <circle cx="50" cy="50" r="20" fill="white" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>100%</span>
              </div>
            </div>
          </motion.div>

          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {FUND_ALLOCATION.map((item) => (
              <div key={item.category} className="flex items-center gap-2">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-sm text-charcoal/70">{item.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl mb-4" style={{ color: '#1A1A1A' }}>
              Our Commitment to You
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {COMMITMENTS.map((commitment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 p-4 bg-green-50 rounded-xl"
              >
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span style={{ color: '#1A1A1A' }}>{commitment}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Annual Reports */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 md:p-12 text-center"
          >
            <FileText size={48} className="mx-auto mb-4 text-blue-600" />
            <h2 className="font-serif text-2xl mb-4" style={{ color: '#1A1A1A' }}>
              Annual Reports
            </h2>
            <p className="text-charcoal/70 mb-6">
              Our annual giving reports are available for review. Contact us for a copy 
              or check back here as we publish new reports.
            </p>
            <p className="text-sm text-charcoal/50 italic">
              Annual reports coming soon
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-6" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="font-serif text-3xl mb-4">Questions About Our Finances?</h2>
          <p className="text-white/80 mb-8">
            Have a question about how funds are used? We welcome the conversation.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white font-semibold transition-transform hover:scale-105"
            style={{ color: '#1A1A1A' }}
            data-testid="transparency-contact-btn"
          >
            <Mail size={20} />
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}
